import type { ComponentChildren, CSSProperties } from 'preact'
import { useMemo, useState } from 'preact/hooks'

import {
  closeIconButtonStyle,
  outlineButtonStyle,
  primaryButtonStyle,
  secondaryButtonStyle,
} from '../buttonStyles'

import { uiTokens } from '#src/assets/ui/shared/styles.ts'

export function Button({
  type = 'button',
  variant = 'primary',
  style,
  className,
  hoverBackground,
  hoverColor,
  onClick,
  children,
  'aria-label': ariaLabel,
}: Partial<{
  type: HTMLButtonElement['type']
  variant: 'primary' | 'secondary' | 'closeIcon' | 'outline'
  style: CSSProperties
  className: string
  hoverBackground: string
  hoverColor: string
  onClick: (event: MouseEvent | PointerEvent) => void
  children: ComponentChildren
  'aria-label': string
}>) {
  const [isHovered, setIsHovered] = useState(false)

  const buttonStyle = useMemo(() => {
    switch (variant) {
      case 'primary':
        return primaryButtonStyle
      case 'secondary':
        return secondaryButtonStyle
      case 'closeIcon':
        return closeIconButtonStyle
      case 'outline':
        return outlineButtonStyle
    }
  }, [variant])

  const resolvedHoverBackground =
    hoverBackground ??
    (variant === 'primary'
      ? uiTokens.colors.gray900
      : variant === 'secondary'
        ? uiTokens.colors.gray150
        : undefined)
  const resolvedHoverColor =
    hoverColor ?? (hoverBackground ? uiTokens.colors.white : undefined)

  return (
    <button
      type={type}
      className={className}
      aria-label={ariaLabel}
      style={{
        ...buttonStyle,
        ...(isHovered && resolvedHoverBackground
          ? {
              background: resolvedHoverBackground,
              ...(resolvedHoverColor ? { color: resolvedHoverColor } : {}),
            }
          : {}),
        ...style,
      }}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {children}
    </button>
  )
}
